import { createContext, useContext } from 'react';
import { useAuth } from '@/features/auth/context';
import { canAccessPortal, type PortalKey } from './portals';
import type { AccessProfile, PermissionCode, RoleCode } from './types';

/**
 * Portal preview ("view as role"), for administrators holding portal.preview.
 *
 * The preview only changes what the INTERFACE shows: navigation, portals and
 * screens as the chosen role would see them. Every request still carries the
 * administrator's own session, so the database answers with the
 * administrator's own access — never more. The previewed permission set is
 * also intersected with the administrator's own, so a preview can never show
 * a screen the administrator could not open. Previews are read-only.
 */
export interface PreviewValue {
  /** Role being previewed, or null when not previewing. */
  role: RoleCode | null;
  /** True while a preview is active: portal screens are read-only. */
  active: boolean;
  /** Access profile the portals use (the preview profile while previewing). */
  access: AccessProfile | null;
  /** The signed-in user's own access profile. */
  realAccess: AccessProfile | null;
  /** Whether the signed-in user may preview portals. */
  canPreview: boolean;
  start: (role: RoleCode) => Promise<boolean>;
  stop: () => void;
}

export const PreviewContext = createContext<PreviewValue | null>(null);

const noPreview = async () => false;
const noop = () => undefined;

/** Preview-aware access. Without a PreviewProvider (e.g. isolated tests) it is the user's own access. */
export function usePortalAccess(): PreviewValue {
  const ctx = useContext(PreviewContext);
  const { access } = useAuth();
  return ctx ?? { role: null, active: false, access, realAccess: access, canPreview: false, start: noPreview, stop: noop };
}

/**
 * Access for one portal. A preview applies only inside the portals the
 * previewed role can open; the administrator's other portals (for example the
 * admin control center that started the preview) keep their own access.
 */
export interface PortalScope {
  access: AccessProfile | null;
  /** True when this portal is shown as the previewed role sees it (read-only). */
  previewing: boolean;
}

export function accessForPortal(value: PreviewValue, portal: PortalKey): PortalScope {
  if (value.active && value.access && canAccessPortal(value.access, portal)) {
    return { access: value.access, previewing: true };
  }
  return { access: value.realAccess, previewing: false };
}

/** Provided by the portal layout for the portal being shown. */
export const PortalScopeContext = createContext<PortalScope | null>(null);

/** Access of the portal being shown (preview-aware), for route guards and screens inside a portal. */
export function usePortalScope(): PortalScope {
  const scope = useContext(PortalScopeContext);
  const { realAccess } = usePortalAccess();
  return scope ?? { access: realAccess, previewing: false };
}

/** Portal screens are read-only while an administrator previews them. */
export function useReadOnly(): boolean {
  return useContext(PortalScopeContext)?.previewing ?? false;
}

/**
 * The access profile a previewed role would get — never more than the
 * administrator's own: permissions are the intersection of both sets.
 */
export function previewAccess(
  real: AccessProfile,
  role: RoleCode,
  rolePermissions: readonly string[],
  portalPath: string | null,
): AccessProfile {
  const own = new Set<string>(real.permissions);
  const ownGlobal = new Set<string>(real.globalPermissions ?? real.permissions);
  const permissions = rolePermissions.filter((p) => own.has(p)) as PermissionCode[];
  const kept = new Set<string>(permissions);
  return {
    ...real,
    roles: [role],
    inheritedRoles: [],
    bundles: [],
    permissions,
    globalPermissions: permissions.filter((p) => ownGlobal.has(p)),
    scopes: (real.scopes ?? []).filter((s) => kept.has(s.permission)),
    primaryPortal: portalPath,
    passwordResetRequired: false,
    preview: role,
  };
}
